/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The checkout's two cards.
 *
 * The screen used to be four stacked panels — plan details, payer data,
 * summary, payment — each with its own heading, and the payment one spent the
 * whole of the form being a dashed rectangle saying what was still missing.
 * It is two cards now: everything that is filled in on the left, everything
 * that is owed on the right, and the card fields live in the left card where
 * the rest of the typing happens rather than appearing at the end.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl, Text, TextInput, UnstyledButton } from '@/shared/ui';
import { documentMask } from '@/shared/utils/input-masks';
import type { PlanSegment } from '@/features/pricing';
import type { BillingCycle, PlanWithFrequency } from '../../hooks/use-plans';
import type { TFunction } from 'i18next';
import type { ZipCodeAddress, ZipCodeStatus } from '../../hooks/use-zip-code';
import type { AppliedCoupon } from './Checkout.coupon';
import { MARK, type ResolvedFeatureRow } from './PlanChoice.catalog';
import { rowValueText } from './PlanChoice.parts';
import { TrialEligibilityNotice } from './PlanChoice.trialNotice';
import type { TrialStatus } from '../../hooks/use-trial-eligibility';
import type {
  CheckoutForm, PriceBreakdown, RequiredField, SummaryLine, SupportSeatLine,
} from './Checkout.pricing';
import {
  MONTHS_PER_YEAR, SUBSCRIPTION_SEAT_FLOOR, seatRange,
} from './Checkout.pricing';

/** Digits a CEP carries, after which the address lookup can run. */
const CEP_DIGITS = 8;

/** Columns the field grid is laid out on, as the handoff draws it. */
const GRID_COLUMNS = 12;

/**
 * Keeps only digits, then lays them into a mask.
 *
 * @param value - Whatever the customer typed.
 * @param pattern - The mask, with `0` standing for a digit.
 * @returns The masked value.
 */
function mask(value: string, pattern: string): string {
  const digits = (value || '').replace(/\D/g, '');
  let out = '';
  let index = 0;
  for (const slot of pattern) {
    if (index >= digits.length) break;
    out += slot === '0' ? digits[index++] : slot;
  }
  return out;
}

/** The masks that are the same whatever is being bought. */
const MASKS: Record<string, string> = { cep: '00000-000' };

/**
 * Masks one field, picking the phone's shape by how much was typed.
 *
 * @param field - Which field is being edited.
 * @param value - The raw value.
 * @returns The masked value.
 */
function maskField(field: keyof CheckoutForm, value: string): string {
  // The document picks its own mask from what has been typed so far,
  // because either one is accepted here and the segment does not know.
  if (field === 'document') return documentMask(value).masked;
  if (field === 'phone') {
    // A mobile number has nine digits after the area code and a landline
    // eight, and the longer mask strands a digit in the wrong group.
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return mask(digits, digits.length > 10 ? '(00) 00000-0000' : '(00) 0000-0000');
  }
  const pattern = MASKS[field];
  return pattern ? mask(value, pattern) : value;
}

const BackArrow: React.FC = () => (
  <svg
    width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

/**
 * The step's title, and the way back to the grid.
 *
 * @param props - The back handler.
 * @returns The header.
 */
export const CheckoutHeader: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <div className="checkout-header" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3-5)' }}>
      <UnstyledButton
        onClick={onBack}
        aria-label={t('checkoutStep.back')}
        style={{
          width: 40, height: 40, display: 'flex', alignItems: 'center',
          justifyContent: 'center', borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-raised)', color: 'var(--text-primary)',
          cursor: 'pointer', flex: 'none',
        }}
      >
        <BackArrow />
      </UnstyledButton>
      <Text
        component="h1"
        style={{
          margin: 0, fontFamily: 'var(--font-family-alt-2)',
          fontSize: 'var(--text-app-title)', fontWeight: 'var(--font-bold)',
          letterSpacing: '-0.02em', color: 'var(--text-primary)',
        }}
      >
        {t('checkoutStep.title')}
      </Text>
    </div>
  );
};

/**
 * One of the two cards the screen is made of.
 *
 * @param props - The flex basis, the inner gap and the contents.
 * @returns The card.
 */
const Card: React.FC<{
  grow: number; basis: number; gap: string; children: React.ReactNode;
  testId?: string;
}> = ({ grow, basis, gap, children, testId }) => (
  <section
    data-testid={testId}
    style={{
      flex: `${grow} 1 ${basis}px`, minWidth: 0,
      display: 'flex', flexDirection: 'column', gap,
      background: 'var(--surface-raised)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--space-4) var(--space-5)',
    }}
  >
    {children}
  </section>
);

/** The left card: everything the buyer fills in. */
export const FormCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Card grow={999} basis={440} gap="var(--space-3)" testId="checkout-form-card">
    {children}
  </Card>
);

/** The right card: everything the buyer owes. */
/**
 * The right card: everything the buyer owes.
 *
 * It hugs its contents instead of matching the form's height. Stretched, it
 * had to find something to do with the leftover — and on a solo purchase,
 * where one summary line faces a whole form, that leftover was most of the
 * card: a column of nothing under the legal text, or a hole above the total,
 * depending on where the slack was spent. There is no information to reach
 * the bottom with, so it does not reach.
 *
 * @param props - The card's contents.
 * @returns The summary card, sized to what it says.
 */
const SummaryShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="checkout-summary-shell"
    style={{ display: 'flex', alignSelf: 'flex-start', flex: '1 1 300px', minWidth: 0 }}
  >
    <Card grow={1} basis={300} gap="var(--space-2-5)" testId="checkout-summary-card">
      {children}
    </Card>
  </div>
);

/**
 * Anual or Mensal, as one control rather than two.
 *
 * The label beside it is the seat instruction on an agency and the control's
 * own name on solo, where there are no seats to instruct about.
 *
 * @param props - The cycle, the saving it carries and the change handler.
 * @returns The cycle row.
 */
/**
 * How a control reads while the charge is in flight.
 *
 * `SegmentedControl` has no disabled prop, and giving it one is a design
 * system change for one screen's benefit. Blocking the pointer and dimming
 * says the same thing from the outside, and says it for every item at once.
 */
const DISABLED_CONTROL: React.CSSProperties = { pointerEvents: 'none', opacity: 0.55 };

export const CycleRow: React.FC<{
  cycle: BillingCycle;
  onCycle: (cycle: BillingCycle) => void;
  segment: PlanSegment;
  discountPercent: number | string;
  disabled?: boolean;
  className?: string;
  radiogroupClassName?: string;
}> = ({
  cycle, onCycle, segment, discountPercent, disabled = false,
  className, radiogroupClassName,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <div
      className={className}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 'var(--space-3)', flexWrap: 'wrap',
      }}
    >
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-meta)', fontWeight: 'var(--font-medium)',
          color: 'var(--text-secondary)',
        }}
      >
        {segment === 'agency'
          ? t('checkoutStep.details.seatsPerPlan', { min: SUBSCRIPTION_SEAT_FLOOR })
          : t('checkoutStep.details.cycle')}
      </Text>
      {/*
        * The design system's segmented control, which is what the plan
        * chooser already draws for this very choice -- monthly against
        * yearly, same two options, one screen earlier. What stood here was
        * a hand-rolled radiogroup: a fully rounded pill on a tertiary
        * ground, and neither the radius nor the beige belongs to anything
        * else on this screen. Two controls for one decision should not look
        * like two different products.
        *
        * The discount rides in the yearly label, as it does on the chooser.
        * `SegmentedControl` takes plain strings, and a separate badge was
        * what forced the bespoke markup in the first place.
        */}
      <div className={radiogroupClassName} style={{ flex: 'none', ...(disabled ? DISABLED_CONTROL : null) }}>
        <SegmentedControl
          value={cycle}
          onChange={(next) => { if (!disabled) onCycle(next as BillingCycle); }}
          items={[
            {
              value: 'yearly',
              label: `${t('planChoice.cycle.yearly')} ${t('planChoice.cycle.discount', { percent: discountPercent })}`,
            },
            { value: 'monthly', label: t('planChoice.cycle.monthly') },
          ]}
          variant="solid"
          size="sm"
          ariaLabel={t('checkoutStep.details.cycle')}
          testId="checkout-cycle-control"
          // A billing cycle is one choice out of two, not a pair of views to
          // navigate between: it is a radiogroup, and that is the role a
          // screen reader (and the checkout-payer E2E, which switches the
          // cycle to open a fresh session) reaches it by.
          selectionRole="radio"
        />
      </div>
    </div>
  );
};

/**
 * One step of a seat counter.
 *
 * @param props - The sign, whether it may be pressed and what it does.
 * @returns The button.
 */
const StepButton: React.FC<{
  sign: 'minus' | 'plus'; aria: string; disabled: boolean; onClick: () => void;
}> = ({ sign, aria, disabled, onClick }) => (
  <UnstyledButton
    onClick={onClick}
    disabled={disabled}
    aria-label={aria}
    style={{
      // The box stays 30x26: it is the target a finger has to hit, and it
      // is not what looked wrong. Only the mark inside it shrank.
      width: 30, height: 26, borderRadius: 'var(--radius-md)', border: 0,
      background: 'transparent', lineHeight: 1,
      color: disabled ? 'var(--border-medium)' : 'var(--text-primary)',
      cursor: disabled ? 'default' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {/*
     * Drawn, not typed.
     *
     * These were the characters `−` and `+` set at `--text-app-heading`:
     * 16px, against a seat count of 13 beside them. An operator louder than
     * the number it changes reads as the loudest thing in the card, and the
     * card is about the plan, not about its buttons. A glyph also arrives at
     * whatever width the font gives it -- U+2212 is a wide one -- so the
     * two marks were not even the same size as each other.
     *
     * Eleven pixels of stroke, like the marks in the summary and beside the
     * address: one line for minus, two for plus, same weight, same colour
     * as the text they sit in.
     */}
    <svg
      width={11} height={11} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
      aria-hidden="true" style={{ flex: 'none' }}
    >
      <path d="M5 12h14" />
      {sign === 'plus' && <path d="M12 5v14" />}
    </svg>
  </UnstyledButton>
);

/**
 * A counter per plan, because a team may mix tiers.
 *
 * An agency does not pick one tier for everybody: the ones left at zero are
 * plans the team does not use. The whole row turns red once the buyer has
 * tried to pay with fewer seats than a subscription sells.
 *
 * @param props - The plans, the chosen seats, the cycle and the handler.
 * @returns The seat cards.
 */
export const SeatCards: React.FC<{
  plans: PlanWithFrequency[];
  seatsBy: Record<string, number>;
  cycle: BillingCycle;
  onSeats: (planKey: string, seats: number) => void;
  /** True once the buyer tried to pay with too few seats. */
  short: boolean;
  disabled?: boolean;
}> = ({ plans, seatsBy, cycle, onSeats, short, disabled = false }) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  const money = (v: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  }).format(v);
  return (
    <div
      data-testid="checkout-seat-cards"
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 'var(--space-2)',
      }}
    >
      {plans.map((plan) => {
        const seats = seatsBy[plan.key] ?? 0;
        const monthly = plan.monthlyPrice ?? plan.price ?? 0;
        const perMonth = cycle === 'yearly' && plan.yearlyPrice != null
          ? plan.yearlyPrice / MONTHS_PER_YEAR
          : monthly;
        return (
          <div
            key={plan.key}
            data-testid={`checkout-seat-${plan.key}`}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)',
              borderRadius: 'var(--radius-lg)', minWidth: 0,
              ...seatCardEdge(seats > 0, short),
            }}
          >
            {/*
             * Name and price share one line now, and the per-seat/month
             * unit moved up to the section label (see seatsPerPlan): at a
             * third of the row's width "R$ 1.478,97/cadeira/mês" does not
             * fit beside the plan name, which is why this used to be a
             * third line. The tier icon is gone too — the user picked the
             * plainer two-line card from the mockup over the three-line one.
             */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-1-5)', minWidth: 0 }}>
              <Text
                component="span"
                style={{
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)',
                }}
              >
                {plan.title}
              </Text>
              <Text
                component="span"
                style={{ flex: 'none', fontSize: 'var(--text-meta)', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}
              >
                {money(perMonth)}
              </Text>
            </div>
            {/*
              * The three parts stand together, not at opposite walls.
              *
              * `space-between` pinned the minus and the plus to the ends of
              * a 187px bar and left the seat count alone in the middle:
              * sixty pixels of empty ground between the control and the
              * number it controls, on each side. Nothing about that reads as
              * one stepper -- it reads as a wide beige bar with a mark in
              * each corner, which is why making the marks smaller did not
              * help. The bar keeps the card's width, because it is the
              * card's own base; the control inside it is centred and tight,
              * so minus, count and plus are one object the eye takes in at
              * once.
              */}
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 'var(--space-2)',
                background: 'var(--surface-card)', borderRadius: 'var(--radius-md)',
              }}
            >
              <StepButton
                sign="minus"
                aria={t('checkoutStep.details.removeSeat', { plan: plan.title })}
                disabled={disabled || seats <= 0}
                onClick={() => onSeats(plan.key, seats - 1)}
              />
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {seats}
              </Text>
              <StepButton
                sign="plus"
                aria={t('checkoutStep.details.addSeat', { plan: plan.title })}
                disabled={disabled || seats >= seatRange(plan).max}
                onClick={() => onSeats(plan.key, seats + 1)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * The border a seat card draws, which says three different things.
 *
 * The padding moves with the border width so a card does not shift by a pixel
 * when its first seat is added.
 *
 * @param chosen - Whether this plan has seats.
 * @param short - Whether the subscription is under its seat floor.
 * @returns The border and the padding that compensates for it.
 */
function seatCardEdge(chosen: boolean, short: boolean): React.CSSProperties {
  if (short) {
    return { border: '1px solid var(--status-error)', padding: '8px 10px' };
  }
  if (chosen) return { border: '2px solid var(--seed-base)', padding: '7px 9px' };
  return { border: '1px solid var(--border-subtle)', padding: '8px 10px' };
}

/**
 * One box in the field grid.
 *
 * Written out rather than taken from `@/shared/ui`: the shared `TextInput`
 * is a 44px-tall control and this screen is drawn at 36, with the size held
 * in a custom property so the whole band can move in one line — see the note
 * on `HOST_FIELD_TOKENS` in `Checkout.tsx`.
 *
 * @param props - The field's identity, value, shape and span.
 * @returns The labelled input.
 */
export const Field: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  span: number;
  inputMode?: 'text' | 'tel' | 'numeric';
  autoComplete?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** Shown on hover, for a value the box is too narrow to finish. */
  title?: string;
  testId?: string;
}> = ({
  id, label, value, onChange, placeholder, span, inputMode, autoComplete,
  invalid = false, disabled = false, title, testId,
}) => (
  <TextInput
    id={id}
    label={label}
    value={value}
    disabled={disabled}
    readOnly={!onChange}
    placeholder={placeholder}
    inputMode={inputMode}
    autoComplete={autoComplete}
    // Not the component's own `error` prop: that one wants a sentence to
    // print under the field, and this screen says what is wrong once, under
    // the total, rather than five times down the form.
    aria-invalid={invalid || undefined}
    // `TextInput` passes native input attributes through, so this reaches
    // the element itself rather than the wrapper around it.
    title={title}
    data-testid={testId}
    onChange={(event) => onChange?.(event.target.value)}
    className="checkout-field"
    style={{ gridColumn: `span ${span}`, minWidth: 0 }}
  />
);

/**
 * Only what Payien needs to register the subscription and invoice it.
 *
 * The document asked for here is the agency's (or the solo broker's own,
 * on that segment) — it becomes the CNPJ the invoice is issued on. The
 * payment frame used to ask for a second document, the cardholder's, which
 * routinely differed from this one; that field was dropped from the frame,
 * so this is the only document on the whole screen now.
 *
 * @param props - The segment, the values, the lookup and what is still wrong.
 * @returns The payer fields.
 */
export const PayerFields: React.FC<{
  segment: PlanSegment;
  form: CheckoutForm;
  onField: (field: keyof CheckoutForm, value: string) => void;
  zipStatus: ZipCodeStatus;
  address: ZipCodeAddress | null;
  /** Fields to draw in red, which is nothing until the buyer tried to pay. */
  invalid: RequiredField[];
  /** A trial without a card asks for no address at all. */
  withAddress?: boolean;
  /**
   * Whether to ask for the name at all.
   *
   * Hidden only for a card-less path that already has one. A solo broker's
   * box is the office name: the account name merely prefills it.
   */
  withName?: boolean;
  disabled?: boolean;
  /**
   * Fields that come from the agency's own record and cannot be retyped.
   *
   * Per field, not all-or-nothing: an agency that exists is buying a plan
   * for itself, so what it already filled in is shown rather than asked for
   * — but a record missing its document or postcode still has to be able to
   * supply one here, or the checkout can never be completed.
   */
  lockedFields?: readonly RequiredField[];
}> = ({
  segment, form, onField, zipStatus, address, invalid,
  withAddress = true, withName = true, disabled = false, lockedFields = [],
}) => {
  const { t } = useTranslation('@pages/onboarding');
  const solo = segment === 'solo';
  const bad = (field: RequiredField) => invalid.includes(field);
  const set = (field: RequiredField) => (
    lockedFields.includes(field)
      ? undefined
      : (value: string) => onField(field, maskField(field, value))
  );
  return (
    <div
      data-testid="checkout-payer-fields"
      className="checkout-grid12"
      style={{
        display: 'grid', gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
        gap: 'var(--space-2-5) var(--space-3)',
      }}
    >
      {withName && (
        <Field
          id="checkout-name"
          // Five, not six. A masked CNPJ is "33.444.555/0001-22" and needs
          // 168px; three columns give 152, so it was cut by sixteen. The
          // name is the field with room to spare -- it still holds about
          // thirty characters at this width -- so it is the one that cedes.
          span={5}
          label={t(solo ? 'checkoutStep.payer.officeName' : 'checkoutStep.payer.agencyName')}
          placeholder={t(solo ? 'checkoutStep.payer.phOfficeName' : 'checkoutStep.payer.phAgencyName')}
          value={form.name}
          onChange={set('name')}
          inputMode="text"
          autoComplete="organization"
          invalid={bad('name')}
          disabled={disabled}
        />
      )}
      <Field
        id="checkout-document"
        // Four beside the name, six without it: a card-less trial does not
        // ask for a name, and three columns of a twelve-column grid would
        // leave half the row empty rather than merely narrow.
        span={withName ? 4 : 6}
        // The "· da imobiliária" / "· da conta" suffix used to disambiguate
        // this from the cardholder's document inside the payment frame.
        // That second field is gone, so the plain label is unambiguous again.
        label={t('checkoutStep.payer.document')}
        placeholder={t('checkoutStep.payer.phDocument')}
        value={form.document}
        // Hover shows it whole. The column fits a CNPJ now, but a field
        // sized to its longest known value has no margin for a wider digit
        // or a reader's own font, and a document read half-way is worse
        // than one not read at all.
        title={form.document || undefined}
        onChange={set('document')}
        inputMode="numeric"
        // No autofill token exists for a CPF or a CNPJ, and the nearest ones
        // the browser offers are all wrong: heuristics have filled this box
        // with a saved card number before now.
        autoComplete="off"
        invalid={bad('document')}
        disabled={disabled}
      />
      <Field
        id="checkout-phone"
        span={withName ? 3 : 6}
        label={t('checkoutStep.payer.phone')}
        placeholder={t('checkoutStep.payer.phPhone')}
        value={form.phone}
        // Two pixels of slack at eleven digits, measured. That is not a fit,
        // it is a coincidence.
        title={form.phone || undefined}
        onChange={set('phone')}
        // 'tel' and not 'numeric': the phone keypad carries the parentheses
        // and the hyphen the mask writes back.
        inputMode="tel"
        // National, not 'tel': the mask here is the Brazilian one and a
        // stored +55 number would be filled in a shape it cannot hold.
        autoComplete="tel-national"
        invalid={bad('phone')}
        disabled={disabled}
      />
      {withAddress && (
        <>
          <Field
            id="checkout-cep"
            span={4}
            label={t('checkoutStep.payer.cep')}
            placeholder={t('checkoutStep.payer.phCep')}
            value={form.cep}
            onChange={set('cep')}
            inputMode="numeric"
            autoComplete="postal-code"
            invalid={bad('cep')}
            disabled={disabled}
          />
          <Field
            id="checkout-number"
            span={2}
            label={t('checkoutStep.payer.number')}
            placeholder={t('checkoutStep.payer.phNumber')}
            value={form.number}
            onChange={set('number')}
            inputMode="numeric"
            // Brazil splits the street from its number and the standard does
            // not: 'address-line1' holds both, so nothing stored fits.
            autoComplete="off"
            invalid={bad('number')}
            disabled={disabled}
          />
          <AddressLine status={zipStatus} address={address} />
        </>
      )}
    </div>
  );
};

/**
 * The street the CEP resolved, on the same line as the CEP.
 *
 * It is not a field: nothing here is typed. The buyer has to see the lookup
 * landed on their own street before paying for an invoice addressed to it.
 *
 * @param props - The lookup's state and what it found.
 * @returns The address line.
 */
const AddressLine: React.FC<{
  status: ZipCodeStatus; address: ZipCodeAddress | null;
}> = ({ status, address }) => {
  const { t } = useTranslation('@pages/onboarding');
  const resolved = status === 'success' && address != null;
  // Nothing to narrate until the buyer engages the CEP. On a fresh checkout
  // -- and on every re-entry, which hands back an empty form -- the lookup is
  // idle with no address, and a slot standing here is read as the previous
  // visit's street rather than as an idle hint (BUG-009). Making it a field
  // did not change that: an empty box with a label is still something to
  // mistake for a value that was filled in before.
  if (status === 'idle' && address == null) return null;
  /*
   * A field, like everything else on this row.
   *
   * This was a bare line of text hanging off the end of the row, bottom-
   * aligned against the inputs beside it: no label, no box, no ground. It
   * was the one thing in a form of labelled fields that was not one, and it
   * appeared out of nothing the moment a CEP resolved -- which is also what
   * made the row jump. Read-only, because the street is not typed here; it
   * is what the CEP answered, and `Field` draws exactly that when no
   * `onChange` reaches it.
   *
   * What used to be the narration of the lookup -- searching, not found,
   * failed -- is the placeholder inside it now.
   */
  const hint = resolved ? '' : zipDescription(status, t);
  return (
    <Field
      id="checkout-address"
      testId="checkout-address-line"
      span={6}
      label={t('checkoutStep.payer.address')}
      placeholder={hint}
      value={resolved ? addressText(address) : ''}
      // A street, a district and a city do not fit six columns, and the box
      // cut "São José dos Campos/SP" mid-word with no way to read the rest:
      // the field is read-only, so there is no caret to walk along it.
      title={resolved ? addressText(address) : undefined}
      disabled={!resolved}
    />
  );
};

/**
 * The resolved address, as one line.
 *
 * @param address - What the lookup found.
 * @returns The street, the neighbourhood, the city and the state.
 */
function addressText(address: ZipCodeAddress): string {
  const line = [address.street, address.neighborhood].filter(Boolean).join(', ');
  return `${line} · ${address.city}/${address.state}`;
}

/**
 * What to say beside the CEP while it has not resolved.
 *
 * @param status - The lookup's state.
 * @param t - The onboarding translator.
 * @returns The line.
 */
function zipDescription(status: ZipCodeStatus, t: TFunction): string {
  if (status === 'loading') return t('checkoutStep.payer.cepSearching');
  if (status === 'error') return t('checkoutStep.payer.cepFailed');
  if (status === 'not_found') return t('checkoutStep.payer.cepNotFound');
  return t('checkoutStep.payer.cepFillsAddress');
}

/**
 * The four things the plan gives, stated beside what it costs.
 *
 * The price lines say how much; this says what for.
 *
 * @param props - The resolved rows and the plan they belong to.
 * @returns The highlights block, or nothing when there are none.
 */
/** The height below which the summary gives up its feature list. */
const SHORT_VIEWPORT_QUERY = '(max-height: 700px)';

/**
 * Whether the window is too short to show the whole summary.
 *
 * Browser zoom shrinks the viewport in CSS pixels, so this is also what a
 * buyer at 150% or 175% sees.
 *
 * @returns True while the viewport is short.
 */
function useShortViewport(): boolean {
  const query = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(SHORT_VIEWPORT_QUERY)
    : null;
  const [short, setShort] = useState(Boolean(query?.matches));
  useEffect(() => {
    if (!query) return undefined;
    const onChange = () => setShort(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return short;
}

const SummaryHighlights: React.FC<{ rows: ResolvedFeatureRow[]; planName: string }> = ({
  rows, planName,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  const short = useShortViewport();
  const [expanded, setExpanded] = useState(false);
  if (!rows.length) return null;
  const title = t('checkoutStep.summary.highlightsOf', { plan: planName });
  const titleStyle: React.CSSProperties = {
    fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
    color: 'var(--text-secondary)',
  };
  // On a short screen the list is what gives way: it describes the plan the
  // buyer already chose, and the total and the pay button must stay on screen.
  if (short && !expanded) {
    return (
      <>
        <Divider tone="ultra" />
        <UnstyledButton
          onClick={() => setExpanded(true)}
          aria-expanded={false}
          data-testid="checkout-highlights-toggle"
          style={{ ...titleStyle, alignSelf: 'flex-start', background: 'none', cursor: 'pointer' }}
        >
          {t('checkoutStep.summary.highlightsShow', { title })}
        </UnstyledButton>
      </>
    );
  }
  return (
    <>
      <Divider tone="ultra" />
      <Text component="span" style={titleStyle}>
        {title}
      </Text>
      {/*
       * One row per feature, name left / value right — not the 2x2 grid the
       * design handoff shows. The user picked this over two stacked
       * alternatives after seeing all three rendered: it reads closer to the
       * seat lines just above (same space-between shape) and costs less
       * height, even though stacking would have matched the handoff file.
       * Do not "fix" this back to a grid without checking with them first.
       */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {rows.map((row) => (
          <div
            key={row.id}
            style={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              gap: 'var(--space-2-5)',
            }}
          >
            <div style={{ display: 'flex', gap: 5, minWidth: 0 }}>
              <svg
                width={11} height={11} viewBox="0 0 20 20" fill="currentColor"
                aria-hidden="true"
                style={{ flex: 'none', marginTop: 2, color: 'var(--plan-feature-on)' }}
              >
                <path fillRule="evenodd" d={MARK.yes} />
              </svg>
              <Text
                component="span"
                style={{ minWidth: 0, fontSize: 'var(--text-meta)', color: 'var(--text-primary)' }}
              >
                {t(`planChoice.matrix.${row.id}`)}
              </Text>
            </div>
            <Text
              component="span"
              style={{
                flex: 'none', fontSize: 'var(--text-meta)', whiteSpace: 'nowrap',
                color: 'var(--text-secondary)',
              }}
            >
              {rowValueText(row.value, t)}
            </Text>
          </div>
        ))}
      </div>
    </>
  );
};

/** A hairline between two parts of the summary. */
/**
 * What the purchase brings in auxiliary users, free of charge.
 *
 * Sits between the priced rows and the total, and deliberately carries no
 * money: nothing here is added to anything. The breakdown per tier is what
 * makes the number checkable — "6 Rubi × 10" is arithmetic the buyer can
 * redo, where a bare "100 free users" is a claim they have to take on
 * trust.
 *
 * Renders nothing when the basket brings none, which covers solo and any
 * catalogue published before auxiliary users existed.
 */
const SupportSeatsBlock: React.FC<{
  support?: { lines: SupportSeatLine[]; total: number } | null;
}> = ({ support }) => {
  const { t } = useTranslation('@pages/onboarding');
  const lines = support?.lines ?? [];
  const total = support?.total ?? 0;
  if (total <= 0) return null;

  return (
    <>
      <Divider tone="ultra" />
      <div
        data-testid="checkout-support-seats"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)' }}
      >
        {/*
         * The count leads, at the size of a price.
         *
         * Everything else in this column is something the buyer is paying
         * for, stated in small grey type. This is the one line that gives
         * rather than takes, and said in that same type it read as another
         * line item to skim past. The number is what sells it — eight free
         * users beside two paid ones is the whole argument — so it is set
         * like the figures the eye already stops on.
         */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
          <Text
            component="span"
            data-testid="checkout-support-seats-total"
            style={{
              fontFamily: 'var(--font-family-alt-2)',
              fontSize: 'var(--text-h3)', fontWeight: 'var(--font-bold)',
              lineHeight: 1, color: 'var(--status-success-text)',
            }}
          >
            {`+${total}`}
          </Text>
          <Text
            component="span"
            style={{
              minWidth: 0, fontSize: 'var(--text-meta)',
              fontWeight: 'var(--font-semibold)', color: 'var(--text-primary)',
            }}
          >
            {t('checkoutStep.summary.supportSeats.title')}
          </Text>
        </div>

        {lines.map((line) => (
          <div
            key={line.planKey}
            style={{
              display: 'flex', alignItems: 'flex-end',
              justifyContent: 'space-between', gap: 'var(--space-2-5)',
            }}
          >
            <Text
              component="span"
              style={{ minWidth: 0, fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}
            >
              {`${line.seats} ${line.planTitle} × ${line.perSeat}`}
            </Text>
            <Text
              component="span"
              style={{
                flex: 'none', whiteSpace: 'nowrap', fontSize: 'var(--text-meta)',
                color: 'var(--text-secondary)',
              }}
            >
              {line.total}
            </Text>
          </div>
        ))}

        <Text
          component="span"
          style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}
        >
          {t('checkoutStep.summary.supportSeats.note')}
        </Text>
      </div>
    </>
  );
};

const Divider: React.FC<{ tone: 'ultra' | 'subtle' }> = ({ tone }) => (
  <div
    style={{
      height: 1,
      background: tone === 'ultra'
        ? 'var(--border-ultra-subtle)'
        : 'var(--border-subtle)',
    }}
  />
);

/** One row of the summary's price lines. */
interface SummaryRow { label: string; value: string; positive?: boolean }

/**
 * The price lines, built from the seat map.
 *
 * @param args - What is being bought, the arithmetic and the translator.
 * @returns One row per thing the buyer is paying for or saving.
 */
/** Everything the price rows are built out of. */
interface RowInput {
  planLines?: SummaryLine[];
  planName: string;
  yearly: boolean;
  price: PriceBreakdown;
  coupon?: AppliedCoupon | null;
  discountPercent: number | string;
  money: (v: number) => string;
  t: TFunction;
}

/**
 * The rows for what is being bought, before any discount comes off.
 *
 * One per plan the team uses, because "5 cadeiras" over a single total says
 * nothing about which tiers those seats are and the buyer is about to pay
 * for a composition the summary would be hiding. A solo broker has one plan
 * and so gets one line.
 *
 * @param args - The lines, the cycle and the formatter.
 * @returns The plan rows.
 */
function planRows(args: RowInput): SummaryRow[] {
  const { planLines, planName, yearly, price, money, t } = args;
  if (!planLines?.length) {
    return [{
      label: t(
        yearly ? 'checkoutStep.summary.planLineYearly' : 'checkoutStep.summary.planLine',
        { plan: planName },
      ),
      value: money(price.gross),
    }];
  }
  return planLines.map((line) => ({
    label: t(
      yearly ? 'checkoutStep.summary.planSeatsYearly' : 'checkoutStep.summary.planSeats',
      { count: line.seats, plan: line.planTitle, months: MONTHS_PER_YEAR },
    ),
    value: money(line.grossCents),
  }));
}

/**
 * The rows for what comes off, each stated as its own line.
 *
 * Never folded into the total: "you pay ten months and get twelve" is the
 * offer, and one discounted figure hides it.
 *
 * @param args - The arithmetic, the coupon and the formatter.
 * @returns The discount rows, in the order they apply.
 */
function discountRows(args: RowInput): SummaryRow[] {
  const { price, coupon, discountPercent, money, t } = args;
  const rows: SummaryRow[] = [];
  if (price.discount > 0) {
    rows.push({
      // The catalogue's figure, not one derived from the totals: twelve
      // months billed as ten is 16.7%, and a summary saying 17% beside a
      // card that promised 16% reads as a different offer.
      label: t('checkoutStep.summary.yearlyDiscount', { percent: discountPercent }),
      value: `− ${money(price.discount)}`,
      positive: true,
    });
  }
  if (!coupon) return rows;
  rows.push({
    // The percentage rides along when there is one: "− R$ 49,00" is a number
    // the buyer cannot check, and "20%" is one they can.
    label: coupon.percent
      ? t('checkoutStep.summary.couponPercent', { code: coupon.code, percent: coupon.percent })
      : t('checkoutStep.summary.coupon', { code: coupon.code }),
    value: `− ${money(coupon.discount)}`,
    positive: true,
  });
  return rows;
}

/** How a trial is being sold, or `null` when nothing is free. */
export type TrialMode = 'card' | 'nocard' | null;

/**
 * What is being bought, what it costs, and what signing up commits to.
 *
 * There is no button here. The handoff put one beside the total, and it was
 * moved into the payment frame instead: the click that charges a card has to
 * happen inside Payien's own document, or an XSS on this page becomes a
 * charge without a human.
 *
 * @param props - The lines, the coupon control, the highlights and the total.
 * @returns The summary card.
 */
export const CheckoutSummary: React.FC<{
  planName: string;
  segment: PlanSegment;
  cycle: BillingCycle;
  onCycle?: (cycle: BillingCycle) => void;
  disabled?: boolean;
  price: PriceBreakdown;
  coupon?: AppliedCoupon | null;
  couponField?: React.ReactNode;
  highlights?: ResolvedFeatureRow[];
  discountPercent?: number | string;
  lines?: SummaryLine[];
  /**
   * The auxiliary users this basket brings, already counted. Computed by
   * the caller, which holds the catalogue; this component prices nothing
   * and counts nothing.
   */
  support?: { lines: SupportSeatLine[]; total: number } | null;
  /** Which trial is on offer, when one is. */
  trial?: TrialMode;
  /** How many free days it runs, as the catalogue sets them. */
  trialDays?: number;
  /** The day a trial starts billing. */
  trialDate?: string;
  /** What the eligibility lookup says, which decides the notes under the total. */
  trialStatus?: TrialStatus;
  /**
   * The only button this card ever draws.
   *
   * Empty on every path that takes a card: that button belongs inside the
   * payment frame, where the click happens in Payien's own document. A
   * card-less trial has no frame and no charge, so the host owns it there.
   */
  action?: React.ReactNode;
  /** Said under the total once the buyer has tried to pay and could not. */
  error?: string | null;
  /** Replaces the card's contents once the money has moved. */
  paid?: boolean;
}> = ({
  planName, segment, cycle, onCycle, disabled = false, price, coupon, couponField, highlights = [],
  discountPercent = '', lines: planLines, support = null, trial = null, trialDays = 0, trialDate = '',
  trialStatus = 'loading', action = null, error = null, paid = false,
}) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  const money = (v: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  }).format(v);
  if (paid) return <SummaryShell><PaidNotice trial={trial} /></SummaryShell>;

  const yearly = cycle === 'yearly';
  const rowInput: RowInput = {
    planLines, planName, yearly, price, coupon, discountPercent, money, t,
  };
  const rows = [...planRows(rowInput), ...discountRows(rowInput)];
  const owed = coupon?.total ?? price.total;
  const cadence = t(yearly ? 'checkoutStep.summary.yearly' : 'checkoutStep.summary.monthly');

  return (
    <SummaryShell>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <Text
          component="h2"
          style={{
            margin: 0, fontFamily: 'var(--font-family-alt-2)',
            fontSize: 'var(--text-app-heading)', fontWeight: 'var(--font-bold)',
          }}
        >
          {t('checkoutStep.summary.title')}
        </Text>
        {trial && (
          <Text
            component="span"
            data-testid="checkout-trial-badge"
            style={{
              flex: 'none', padding: '3px var(--space-2-5)',
              borderRadius: 'var(--radius-full)',
              background: 'var(--status-success-bg)', color: 'var(--status-success-text)',
              fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)',
            }}
          >
            {t('checkoutStep.trial.badge', { count: trialDays })}
          </Text>
        )}
      </div>

      {/* Under the heading, over the numbers.

          The cycle is the question every figure below it answers -- the plan
          line, the discount and the total are all written in whichever of
          the two is chosen -- so it reads as the summary's first control
          rather than as one more of its details. Above the heading it was a
          control floating over a card it did not belong to yet. */}
      {onCycle && (
        <div className="checkout-cycle-mobile" data-testid="checkout-summary-cycle-mobile">
          <CycleRow
            cycle={cycle}
            onCycle={onCycle}
            segment="solo"
            discountPercent={discountPercent}
            disabled={disabled}
          />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {rows.map((row) => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2-5)' }}>
            <Text
              component="span"
              style={{
                fontSize: 'var(--text-small)',
                color: row.positive ? 'var(--status-success-text)' : 'var(--text-secondary)',
              }}
            >
              {row.label}
            </Text>
            <Text
              component="span"
              style={{
                fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
                whiteSpace: 'nowrap',
                color: row.positive ? 'var(--status-success-text)' : 'var(--text-primary)',
              }}
            >
              {row.value}
            </Text>
          </div>
        ))}
      </div>

      {couponField}

      <SummaryHighlights rows={highlights} planName={planName} />

      {/*
        A fixed breath before the total, not a spring.

        The handoff asks for `flex:1` here, to push the total onto the card's
        foot while both cards are stretched to a common height. That only
        works while there is a foot worth reaching: on a solo checkout one
        summary line faces a whole form, and the spring spent every spare
        pixel — 250px of nothing between the highlights and the price they
        explain. Capping it moved the hole below the legal line instead of
        closing it.

        The card no longer stretches (see `SummaryShell`), so there is no
        surplus to distribute and nothing for a spring to do. A plain gap is
        what is left, and it is the whole of it.
      */}
      <SupportSeatsBlock support={support} />
      <div className="checkout-summary-spacer" style={{ height: 'var(--space-6)' }} />
      <Divider tone="subtle" />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2-5)' }}>
        <Text
          component="span"
          style={{ fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' }}
        >
          {t('checkoutStep.summary.dueToday')}
        </Text>
        <Text
          component="span"
          data-testid="checkout-total"
          style={{
            fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-bold)', color: 'var(--seed-base)',
            letterSpacing: '-0.02em', whiteSpace: 'nowrap',
          }}
        >
          {money(trial ? 0 : owed)}
        </Text>
      </div>

      {trial && (
        <Text
          component="span"
          style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}
        >
          {t(trial === 'card' ? 'checkoutStep.trial.fromDate' : 'checkoutStep.trial.afterTest', {
            date: trialDate,
            total: money(owed),
            cadence,
          })}
        </Text>
      )}

      {/* Only the refusal. The trial's usage cap is said on the plan screen,
          where the plan is chosen; repeated under the total it was one more
          paragraph between the buyer and the pay button. */}
      {trialStatus === 'ineligible' && (
        <TrialEligibilityNotice status={trialStatus} segment={segment} />
      )}

      {action}

      {error && (
        <Text
          component="span"
          role="alert"
          data-testid="checkout-submit-error"
          style={{
            fontSize: 'var(--text-meta)', color: 'var(--status-error-text)',
            textAlign: 'center',
          }}
        >
          {error}
        </Text>
      )}

      <Text
        component="p"
        style={{
          margin: 0, fontSize: 'var(--text-meta)', lineHeight: 1.45,
          color: 'var(--text-tertiary-soft)', textAlign: 'center',
        }}
      >
        {legalText({
          trial, cadence, date: trialDate, total: money(owed), t, chargedToday: trialStatus === 'ineligible',
        })}
      </Text>
    </SummaryShell>
  );
};

/**
 * What signing up commits the buyer to, in the words of the offer taken.
 *
 * @param args - The trial on offer, the cadence, the date and the amount.
 * @returns The legal line.
 */
function legalText(args: {
  trial: TrialMode; cadence: string; date: string; total: string; t: TFunction;
  /** The owner had a subscription before, so the first charge is today's. */
  chargedToday?: boolean;
}): string {
  const { trial, cadence, date, total, t, chargedToday } = args;
  if (trial === 'card') return t('checkoutStep.trial.legalCard', { date, total, cadence });
  if (trial === 'nocard') return t('checkoutStep.trial.legalNoCard', { date });
  if (chargedToday) return t('checkoutStep.noTrial.legal', { total, cadence });
  return t('checkoutStep.summary.renew', { cadence });
}

/**
 * What the summary becomes once the money has moved.
 *
 * @param props - Whether a trial was started rather than a charge taken.
 * @returns The confirmation.
 */
const PaidNotice: React.FC<{ trial: TrialMode }> = ({ trial }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <div
      data-testid="checkout-paid"
      role="status"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 'var(--space-3)', flex: 1,
        padding: 'var(--space-6) 0',
      }}
    >
      <Text
        component="span"
        aria-hidden="true"
        style={{
          width: 40, height: 40, borderRadius: 'var(--radius-full)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--status-success-bg)', color: 'var(--status-success-text)',
          fontSize: 'var(--text-app-heading)', fontWeight: 'var(--font-bold)',
        }}
      >
        ✓
      </Text>
      <Text
        component="span"
        style={{
          fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-app-heading)',
          fontWeight: 'var(--font-bold)',
        }}
      >
        {t(trial ? 'checkoutStep.trial.started' : 'checkoutStep.payment.confirmed')}
      </Text>
    </div>
  );
};

/**
 * The card-less trial's start button.
 *
 * The one button this page draws itself. Every path that takes a card has
 * its button inside the payment frame instead, so this is not a second way
 * to pay — it is the only control on the one flow where nothing is charged.
 *
 * @param props - Whether it is working, and what it does.
 * @returns The button.
 */
export const StartTrialButton: React.FC<{
  busy: boolean; days: number; onClick: () => void;
}> = ({ busy, days, onClick }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <UnstyledButton
      onClick={onClick}
      data-testid="checkout-start-trial"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 'var(--space-2)', width: '100%', height: 42,
        borderRadius: 'var(--radius-md)', border: 0, cursor: 'pointer',
        background: 'var(--button-primary-bg)', color: 'var(--button-primary-text)',
        fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-semibold)',
      }}
    >
      {t(busy ? 'checkoutStep.trial.starting' : 'checkoutStep.trial.start', { count: days })}
    </UnstyledButton>
  );
};

/**
 * The line under the seat cards: what is missing, when something is.
 *
 * @param props - The seats chosen so far.
 * @returns The footer, or nothing when the team is big enough.
 */
export const SeatFloorNotice: React.FC<{ seats: number }> = ({ seats }) => {
  const { t } = useTranslation('@pages/onboarding');
  if (seats >= SUBSCRIPTION_SEAT_FLOOR) return null;
  return (
    <Text
      component="span"
      data-testid="checkout-seat-footer"
      style={{
        fontSize: 'var(--text-meta)', lineHeight: 1.45,
        color: 'var(--status-error-text)',
      }}
    >
      {t('checkoutStep.details.seatsTooFew', { min: SUBSCRIPTION_SEAT_FLOOR })}
    </Text>
  );
};

export { CEP_DIGITS, maskField };
